import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { InfiniteSlider } from "@/components/ui/infinite-slider";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ContainerScroll } from "@/components/ui/container-scroll-animation";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import FooterWithNewsletter from "@/components/ui/footer-column";
import FeatureSection from "@/components/ui/feature-section";
import DiscoverMoreSection from "@/components/DiscoverMoreSection";
import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle, Palette, Share2, Layers, MessageSquare, FileText, Images, Edit3, Rocket, Zap, Search, Star } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateToEnterpriseDemo } from "@/utils/navigation";
import CTASection from "@/components/CTASection";
function StudioPage() {
  const reduce = useReducedMotion();
  const [isReasoningMode, setIsReasoningMode] = useState(false);
  const navigate = useNavigate();
  const whyCards = [{
    icon: Palette,
    title: "On-brand by design",
    description: "Vaults auto-apply your background information, pov, tone of voice and more"
  }, {
    icon: Layers,
    title: "All-in-one",
    description: "Build your research, insights, ideas into various content like copy, visuals, and video in one place"
  }, {
    icon: Share2,
    title: "Fast to publish",
    description: "Edit your results with built-in editors and publish to destinations like LinkedIn, Insta, FB and others"
  }];
  const steps = ["Pick a trained AI model + Brand/Personal Vault", "Prompt (optional improve my prompt) or the prompt library", "Add optional context with file uploads", "Generate copy + visuals + video", "Last-mile finetuning with built-in editors", "Direct publishing to 12 destinations"];
  const testimonials = [{
    quote: "Studio shipped our entire quarterly campaign from one chat. On-brand, no chaos.",
    name: "Maya R.",
    role: "Head of Marketing, SaaS",
    avatar: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/8cc9cb15-6326-49d0-aa0c-7813370044ec/mayo_09.jpg?content-type=image%2Fjpeg"
  }, {
    quote: "From prompt to LinkedIn + IG in minutes. The brand voice lock is gold.",
    name: "Leo T.",
    role: "Founder, DTC",
    avatar: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/c4ef4ae2-d53e-45cc-a206-212fa7c44770/mayo_17.jpg?content-type=image%2Fjpeg"
  }, {
    quote: "Finally one place for copy and visuals. Our content velocity 3×ed.",
    name: "Jules A.",
    role: "Agency Partner",
    avatar: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/caed502e-faec-4a5b-a3e1-3aec4662533e/mayo_18.jpg?content-type=image%2Fjpeg"
  }, {
    quote: "Game-changer for our creative workflow. Studio handles our biggest campaigns.",
    name: "Sam K.",
    role: "Creative Director",
    avatar: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/c6345642-2804-4403-903e-4ec0be2a9edf/AnitaRetayne.jpg?content-type=image%2Fjpeg"
  }];
  const _heroRight = <div className="space-y-3">
      {[{
      Icon: MessageSquare,
      label: "Chat"
    }, {
      Icon: FileText,
      label: "Brief"
    }, {
      Icon: Images,
      label: "Copy + Visuals"
    }, {
      Icon: Edit3,
      label: "Edit"
    }, {
      Icon: Rocket,
      label: "Publish"
    }].map(({
      Icon,
      label
    }, i) => <motion.div key={label} initial={reduce ? undefined : {
      opacity: 0,
      x: 12
    }} whileInView={reduce ? undefined : {
      opacity: 1,
      x: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.4,
      delay: i * 0.08
    }} className="flex items-center gap-3 rounded-xl border bg-background p-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary border border-primary/20 grid place-items-center">
            <Icon className="w-4 h-4" />
          </div>
          <div className="font-medium">{label}</div>
          <CheckCircle className="ml-auto w-4 h-4 text-primary" />
        </motion.div>)}
    </div>;
  return <div className="bg-background mt-24 md:mt-0">
      <Navigation />
      <div style={{
      '--brand': '265 83% 58%',
      '--brand-foreground': '270 95% 70%'
    } as React.CSSProperties}>
        <ContainerScroll titleComponent={<>
              <div className="mb-4">
                <Badge className="bg-primary/10 text-primary border-primary/20 px-3 py-1">
                  Studio
                </Badge>
              </div>
              <h1 className="text-4xl font-semibold text-black dark:text-white mb-4">
              Unified Content. One Chat, One Workspace, One Voice.
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Studio is your marketing content specialist's desk — one chat to bring you from research, to ideation, to copy, visuals and videos.              
              </p>
              <div className="flex justify-center mt-8">
                <Button id="studio-book-demo-button" className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors h-12" onClick={() => navigateToEnterpriseDemo(navigate)}>
                  Book a Studio Demo
                </Button>
              </div>
            </>}>
          <img src="/lovable-uploads/934ac46f-c966-480e-923a-fb538d02db36.jpg" alt="Studio interface" width={963} height={480} className="mx-auto rounded-2xl object-cover h-full w-full" draggable={false} />
        </ContainerScroll>
      </div>

      {/* Section 1: Why Studio is Different */}
      <section className="pt-56 md:pt-64 pb-16">
        <div className="container mx-auto px-6">
          <h2 className="sr-only">Why Studio is Different</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {whyCards.map((c, idx) => <Card key={idx} className="h-full">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary border border-primary/20 grid place-items-center">
                    <c.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold">{c.title}</div>
                    <p className="text-sm text-muted-foreground">{c.description}</p>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Section 2: How Studio Works */}
      <section className="py-12 bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 dark:from-slate-950/20 dark:via-gray-950/20 dark:to-blue-950/20">
        <div className="container mx-auto px-[10%]">
          {/* Centered Title */}
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-foreground">
            How Studio Works
          </h2>

          <div className="grid lg:grid-cols-[1fr_2fr] gap-12 items-stretch">
            {/* Left Column - Steps */}
            <div className="flex flex-col justify-center">
              <div className="space-y-8">
                {steps.map((step, idx) => <motion.div key={idx} initial={reduce ? undefined : {
                opacity: 0,
                x: 20
              }} whileInView={reduce ? undefined : {
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: idx * 0.1
              }} className="flex items-start gap-4 relative justify-end text-right">
                    {/* Connecting Line */}
                    {idx < steps.length - 1 && <div className="absolute right-5 top-12 w-0.5 h-8 bg-border" style={{
                  top: '48px'
                }} />}
                    {/* Step Text */}
                    <div className="flex-1 pt-1">
                      <div className="text-base md:text-lg font-medium text-foreground">
                        {step}
                      </div>
                    </div>
                    {/* Step Circle */}
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold text-white shadow-lg relative z-10 ${idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-orange-500' : idx === 2 ? 'bg-yellow-500' : idx === 3 ? 'bg-green-500' : idx === 4 ? 'bg-blue-500' : 'bg-purple-500'}`}>
                      {idx + 1}
                    </div>
                  </motion.div>)}
              </div>
            </div>

            {/* Right Column - Image */}
            <motion.div initial={reduce ? undefined : {
            opacity: 0,
            scale: 0.95
          }} whileInView={reduce ? undefined : {
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative h-full min-h-[400px]">
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-background h-full">
                <img src="/lovable-uploads/5765aad0-79b3-4f1a-a6f0-ed77e0b33985.jpg" alt="Person using Studio with chat, playbook, and video features displayed" className="w-full h-full object-cover object-left" width={702} height={559} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 3: Improve My Prompt & Reasoning Mode */}
      <section className="py-12" style={{
      backgroundColor: '#ededf4'
    }}>
        <div className="container mx-auto px-6">
          {/* Toggle Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="flex items-center gap-3">
                <Zap className={`w-5 h-5 transition-colors ${!isReasoningMode ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className={`font-medium transition-colors ${!isReasoningMode ? 'text-foreground' : 'text-muted-foreground'}`}>
                  Improve My Prompt
                </span>
              </div>
              <Switch checked={isReasoningMode} onCheckedChange={setIsReasoningMode} className="mx-2" />
              <div className="flex items-center gap-3">
                <Search className={`w-5 h-5 transition-colors ${isReasoningMode ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className={`font-medium transition-colors ${isReasoningMode ? 'text-foreground' : 'text-muted-foreground'}`}>
                  Reasoning Mode
                </span>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <motion.div key={isReasoningMode ? 'reasoning' : 'magic'} initial={reduce ? undefined : {
            opacity: 0,
            y: 20
          }} animate={reduce ? undefined : {
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }}>
              {!isReasoningMode ?
            // Improve My Prompt Content
            <>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">Turn ideas into briefs instantly</h2>
                  <p className="text-muted-foreground mb-6">
                    Improve My Prompt expands a couple of words into a full creative brief. Skip the prompt engineering and get straight to creating.
                  </p>
                  <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                    <li>Skip prompt engineering entirely</li>
                    <li>On-brand by default with Vault referencing</li>
                    <li>Smart contextual expansion based on best practices</li>
                    <li>Fast detailed brief generation</li>
                  </ul>
                </> :
            // Reasoning Mode Content
            <>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">Deep analysis with research backing</h2>
                  <p className="text-muted-foreground mb-6">
                    Reasoning Mode ensures your prompt's solution is given with nuance, deep dive research backing, and analytical thinking. Every step is built on the findings from the previous step.         
                  </p>
                  <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                    <li>Research-backed analysis</li>
                    <li>Strategic context and insights</li>
                    <li>Source citations included</li>
                    <li>Multi-level layered thinking</li>
                  </ul>
                </>}
            </motion.div>

            <motion.div key={`example-${isReasoningMode ? 'reasoning' : 'magic'}`} initial={reduce ? undefined : {
            opacity: 0,
            y: 20
          }} animate={reduce ? undefined : {
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.1
          }} className="space-y-4">
              <div className="rounded-xl border bg-card text-card-foreground p-4">
                <div className="text-xs uppercase text-muted-foreground mb-2">Input</div>
                <pre className="text-sm">summer sale ig post</pre>
              </div>
              <div className="rounded-xl border bg-card text-card-foreground p-4">
                <div className="text-xs uppercase text-muted-foreground mb-2">
                  {!isReasoningMode ? 'Improve My Prompt Output' : 'Reasoning Mode Output'}
                </div>
                {!isReasoningMode ? <pre className="text-sm whitespace-pre-wrap">
                    Create an Instagram post announcing Sony’s Summer Sale on premium headphones. Use an authoritative yet approachable tone that emphasises industry‑leading noise cancellation, immersive sound, up to 50 hours battery life, sleek design and reliable connectivity. Keep copy concise and caption-ready for a carousel, appeal to tech‑savvy listeners, and recommend high-impact visual concepts that convey premium audio and summer lifestyle energy.
                  </pre> : <pre className="text-sm whitespace-pre-wrap">
                    Summer sale campaign analysis: Strategic context: Q2 retail typically sees 15-30% sales decline (NRF data). Summer promotions counteract seasonal shopping patterns. Recommended approach: Multi-channel campaign with social proof emphasis. Research shows 20% discount hits optimal price sensitivity threshold. Deliverables: IG carousel post + reel with performance benchmarking against seasonal retail trends. Sources: National Retail Federation seasonal data, consumer behavior studies.
                  </pre>}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection title="Know someone who needs content magic?" subtitle="Give a friend a head-start—share a special invite to unlock bonus signup credits on gimmefy Studio." buttonText="Tell a friend" buttonLink="/tellafriend" />

      {/* New Section: Workflow Automation */}
      <FeatureSection />

      {/* Section 5: Proof & Testimonials */}
      <section className="py-16 bg-gradient-accent">
        <div className="container mx-auto px-8">
          <motion.div className="text-center mb-8" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }}>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">What teams say</h2>
          </motion.div>

          <div className="relative max-w-full">
            <InfiniteSlider duration={35} durationOnHover={60} gap={16} className="py-4">
              {testimonials.map((testimonial, index) => <motion.div key={index} initial={reduce ? undefined : {
              opacity: 0,
              y: 30
            }} whileInView={reduce ? undefined : {
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1,
              duration: 0.6
            }} className="w-80 h-full flex-shrink-0">
                  <Card className="p-5 h-full bg-gradient-card border-border shadow-elegant hover:shadow-elegant-hover transition-all duration-300">
                    <div className="space-y-3">
                      {/* 5-star rating */}
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, starIndex) => <Star key={starIndex} className="h-4 w-4 text-yellow-400 fill-current" />)}
                      </div>

                      <p className="text-base text-foreground leading-relaxed italic">
                        "{testimonial.quote}"
                      </p>

                      <div className="flex items-center pt-2 border-t border-border/50">
                        <Avatar className="h-8 w-8 mr-3">
                          <AvatarImage width={48} height={48} src={testimonial.avatar} alt={testimonial.name} />
                          <AvatarFallback className="bg-gradient-primary text-white font-bold">
                            {testimonial.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-primary font-semibold text-sm">{testimonial.name}</p>
                          <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>)}
            </InfiniteSlider>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-primary/10 via-primary/5 to-background">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Turn every idea into a live campaign.</h2>
          <Button size="lg" className="mt-4" onClick={() => navigateToEnterpriseDemo(navigate)}>
            Book a Studio walkthrough
          </Button>
        </div>
      </section>

      <DiscoverMoreSection currentPage="studio" />

      <FooterWithNewsletter />
    </div>;
}
export default StudioPage;